import gql from 'graphql-tag';

export const EventCountryQuery = gql`
  query EventQuery($eventId: Int!) {
    event: legacyEvent(eventId: $eventId) {
      id
      title
      place: place {
        address: address {
          countryShort
        }
      }
      slug
    }
  }
`;

export const legacyEventSummaryFragment = gql`
  fragment legacyEventSummaryFragment on LegacyEvent {
    id
    title
    startDate
    organiser {
      title
    }
    images {
      logo {
        thumbnail
      }
      hero {
        large
        medium
      }
    }
    virtualEventDetails {
      isVirtual
      submissionStartDate
      submissionEndDate
    }
    races {
      races {
        id
        bookingEnabled
        information {
          sport
          distance
          metricLength
          raceUnit
          raceLength
        }
        title
      }
    }
  }
`;

export const EventSummary = gql`
  ${legacyEventSummaryFragment}
  query EventSummary($eventId: Int!) {
    event: legacyEvent(eventId: $eventId) {
      ...legacyEventSummaryFragment
    }
  }
`;

export const EventTrackingParamsQuery = gql`
  query EventTrackingQuery($eventId: Int!) {
    event: legacyEvent(eventId: $eventId) {
      id
      title
      startDate
      races {
        bookingEnabled
      }
      organiser {
        title
      }
    }
  }
`;

export const legacyEventCommonFragment = `
  fragment legacyEventCommon on LegacyEvent {
    flagship
    timezone
    cancelled
    postponed
    postponedUntil
    saves {
      total
      users {
        id
      }
    }
    organiser {
      id
      title
      description
      slug
      excerpt
      website
      primaryIdentityIds
      images {
        logo {
          thumbnail
          facebook
          twitter
        }
      }
    }
    upcomingOrganizerEvents {
      id
      title
      slug
      place {
        address {
          countryShort
        }
      }
      reviewSummary {
        total
        ratings {
          overall
        }
      }
    }
    races {
      tags {
        value
        link
      }
      races {
        eventDetails {
          slug
          id
          title
          eventUrl
          images {
            hero {
              large
            }
          }
        }
        bookingEnabled
        bookingPartner {
          value
          id
          typeDescriptor
        }
        tickets {
          charityTickets
          standardTickets
          ballotTickets
          available
          discounts
          bookingEnabled
          bookingPartner {
            value
            id
            typeDescriptor
          }
          pricesFrom
          pricesTo
          currencyCode
          currencyLabel
          currencySymbol
          tickets {
            charityTickets
            standardTickets
            ballotTickets
            id
            title
            description
            availabilityType {
              type
              typeDescriptor
              value
            }
            charityDetails {
              blurb
              charityId
              title
              image
              tags
            }
            availability {
              type
              typeDescriptor
              value
              from
              to
            }
            notifications {
              type
              value
              typeDescriptor
            }

            currencyCode
            currencyLabel
            currencySymbol
            rrp
            fee
            discountPrice
            discountCode
            bookingUrl
            bookingEnabled
            bookingPartner {
              value
              id
              typeDescriptor
            }
            bookingDetails {
              price
              commission
              provider
              discountCode
              zapUrl
              externalId
              fee
              paymentAccount
            }
            ticketStats {
              bookings7Days
            }
            isDeleted
          }

          availabilityType {
            type
            typeDescriptor
            value
            from
            to
          }
        }
        courseMaps {
          alt
          large
          source
        }

        keyDetails {
          type
          id
          typeDescriptor
          value
        }

        tags {
          value
          link
        }

        notifications {
          type
          value
          typeDescriptor
        }

        description
        startDate
        startTime
        excerpt
        endDate
        gpx
        gpxObject {
          sourceUrl
          meta {
            elevation {
              gain
              loss
              totalChange
              maximum
              minimum
            }
            distance
          }
        }
        customContent {
          key
          label
          content
        }

        courseDetails {
          label
          id
        }

        competitorSize
        id
        title

        information {
          sport
          distance
          metricLength
          raceUnit
          raceLength
          sportLabel
          sportSlug
        }
        legDetails {
          id
          discipline
          distance
          distanceUnit
        }
      }

      pricesFrom
      pricesTo
      bookingEnabled
      bookingPartner {
        value
        id
        typeDescriptor
      }
      currencyCode
      currencyLabel
      currencySymbol
      notifications {
        type
        value
        typeDescriptor
      }
    }

    images {
      hero {
        xlarge
        medium
        large
        facebook
        twitter
        title
        thumbnail
      }
      gallery {
        large
      }

      logo {
        large
        thumbnail
        facebook
        twitter
      }
    }
    location {
      lat
      long
      address
    }
    place {
      address {
        formatted
        city
        county
        stateLong
        stateShort
        countryLong
        countryShort
      }
    }
    videos {
      url
    }
    notifications {
      type
      value
      typeDescriptor
    }

    id
    title
    description
    excerpt
    startDate
    slug
    eventUrl
    organiserReviews {
      id
      createdAt
      author {
        displayName
        picture
      }
    }
    eventStats {
      pageVisits7Days
    }
  }
`;
export const reasonsToRecommendFragment = `
  fragment reasonsToRecommendFragment on LegacyEvent {
    reasonsToRecommend {
      STUNNING_SCENERY {
        frequency
        weighting
      }
      FRIENDLY_ATMOSPHERE {
        frequency
        weighting
      }
      INCREDIBLE_CROWDS {
        frequency
        weighting
      }
      FAMILY_FRIENDLY {
        frequency
        weighting
      }
      PERFECT_FOR_A_PB {
        frequency
        weighting
      }
      GOOD_FOR_BEGINNERS {
        frequency
        weighting
      }
      CHALLENGING_COURSE {
        frequency
        weighting
      }
      AMAZING_MEDAL {
        frequency
        weighting
      }
      FESTIVAL_VIBES {
        frequency
        weighting
      }
      HELPFUL_VOLUNTEERS {
        frequency
        weighting
      }
      WELL_ORGANIZED {
        frequency
        weighting
      }
      GREAT_PARKING {
        frequency
        weighting
      }
      GOOD_CAUSE {
        frequency
        weighting
      }
      AWESOME_GOODY_BAG {
        frequency
        weighting
      }
      PUBLIC_TRANSPORT_NEARBY {
        frequency
        weighting
      }
      GREAT_VALUE {
        frequency
        weighting
      }
    }
  }
`;
export const EventQuery = gql`
  ${legacyEventCommonFragment}
  ${reasonsToRecommendFragment}
  query EventQuery($eventId: Int!) {
    event: legacyEvent(eventId: $eventId) {
      ...legacyEventCommon
      ...reasonsToRecommendFragment
      faqs {
        question
        answer
      }
      similarEvents {
        title
        slug
        id
        startDate
        place {
          address {
            city
            countryShort
          }
        }
        images {
          hero {
            thumbnail
          }
        }
        reviews {
          ratings {
            overall
          }
          total
        }
      }
      tags
      badges {
        id
      }
      swag {
        enabled
        title
        description
        images {
          large
          title
        }
      }
      virtualEventDetails {
        isVirtual
        enableVirtualResultSubmission
        virtualEventUrl
        submissionStartDate
        submissionEndDate
        challengeType
      }
      organiserMessage
      isSeries
      seriesOccurrenceId
      seriesStatus
      redirect
    }
  }
`;

export const EventQueryDetails = gql`
  query EventQueryDetails($eventIds: [Int]!) {
    legacyEvents(eventIds: $eventIds) {
      id
      images {
        hero {
          xxlarge
          xlarge
          large
          medium
          thumbnail
          original
          facebook
          twitter
        }
      }
      place {
        address {
          city
          countryShort
        }
      }
      virtualEventDetails {
        challengeType
      }
      title
      races {
        races {
          id
          information {
            raceUnit
            raceLength
          }
        }
      }
    }
  }
`;

export const CloseBookingsMutation = gql`
  ${legacyEventSummaryFragment}
  mutation CloseBookings(
    $eventId: Int!
    $raceId: String
    $reason: String
    $message: String
    $participantMessage: String
  ) {
    closeBookings(
      eventId: $eventId
      raceId: $raceId
      reason: $reason
      message: $message
      participantMessage: $participantMessage
    ) {
      ...legacyEventSummaryFragment
    }
  }
`;
